import { Flex, Typography } from "antd";
import { formatCurrency } from "../../../../../../Utils/utils";
import { formatCount } from "../../../../components/Table/columns";
import Icon from "../../../../../../Icon";
import cls from "classnames";
import MonthRowName from "../../../../components/Monitoring/MonthRowName/MonthRowName";
import { DEFAULT_EPS_ORDERING, withEpsSort } from "../../../../components/Monitoring/epsSorting";
import HighlightedText from "../../../../components/Monitoring/HighlightedText/HighlightedText";
import { formatRiskTooltip, getRiskLevel } from "../../../../components/Monitoring/riskLevel";
import RiskPill from "../../../../components/Monitoring/RiskPill/RiskPill";

const { Text } = Typography;

const getIsValidNumber = (value) => typeof value === "number" && !isNaN(value);

const rateOf = (count, record) =>
  record?.transaction_count ? (count * 100) / record.transaction_count : 0;

const countWithRisk = ({ count, record, rateName, limitName, limit }) => {
  const rate = rateOf(count, record);
  const tooltip = limit ? formatRiskTooltip({ rateName, rate, limitName, limit }) : null;

  return (
    <RiskPill level={getRiskLevel(rate, limit, { withOk: false })} tooltip={tooltip}>
      {formatCount(count)}
    </RiskPill>
  );
};

export const getColumns = ({
  t,
  expandedKeys,
  isMobile,
  selectedMetric,
  limits = {},
  sortable = false,
  search = "",
}) => {
  const isMonth = (record) => record?.row_type === "month";

  const allColumns = [
    {
      key: "billing_descriptor",
      title: t("Billing descriptor"),
      dataIndex: "billing_descriptor",
      render: (_, record) => {
        const row_type = record?.row_type;
        const isExpandable = record?.months?.length > 0 && row_type !== "month";
        const isExpanded = expandedKeys.includes(record?.unique_id);

        if (row_type === "month") return <MonthRowName month={record} />;
        return (
          <Flex gap={8} align="center">
            {isExpandable && (
              <Icon
                type="chevron_down_small"
                className={cls("chevron_down", { expanded: isExpanded })}
              />
            )}
            <Text strong>{_ == null ? "—" : <HighlightedText text={_} query={search} />}</Text>
          </Flex>
        );
      },
    },
    {
      key: "fraud_count",
      title: "Fraud count",
      dataIndex: "fraud_count",
      align: "right",
      render: (_, record) => {
        if (!getIsValidNumber(_)) return <Text strong={!isMonth(record)}>—</Text>;
        if (isMonth(record)) return <Text>{formatCount(_)}</Text>;

        return (
          <Text strong>
            {countWithRisk({
              count: _,
              record,
              rateName: "Fraud rate",
              limitName: "EFM limit",
              limit: limits.efm,
            })}
          </Text>
        );
      },
    },
    {
      key: "dispute_count",
      title: "Dispute count",
      dataIndex: "dispute_count",
      align: "right",
      render: (_, record) => {
        if (!getIsValidNumber(_)) return <Text strong={!isMonth(record)}>—</Text>;
        if (isMonth(record)) return <Text>{formatCount(_)}</Text>;

        return (
          <Text strong>
            {countWithRisk({
              count: _,
              record,
              rateName: "Chargeback rate",
              limitName: "ECM limit",
              limit: limits.ecm,
            })}
          </Text>
        );
      },
    },
    {
      key: "transaction_count",
      title: "Transaction count",
      dataIndex: "transaction_count",
      align: "right",
      render: (_, record) => {
        return <Text strong={!isMonth(record)}>{getIsValidNumber(_) ? formatCount(_) : "—"}</Text>;
      },
    },
    {
      key: "transaction_volume",
      title: "Transaction volume",
      dataIndex: "transaction_volume",
      align: "right",
      render: (_, record) => {
        return (
          <Text strong={!isMonth(record)}>
            {getIsValidNumber(_) ? formatCurrency(_, true) : "—"}
          </Text>
        );
      },
    },
  ];

  const columns = allColumns.map((column) =>
    withEpsSort(column, {
      sortable,
      isText: column.key === "billing_descriptor",
      defaultOrdering: DEFAULT_EPS_ORDERING,
    }),
  );

  if (isMobile) {
    return columns.filter(
      (col) => col.key === "billing_descriptor" || col.key === selectedMetric,
    );
  }

  return columns;
};
